/**
 * Shared shaping + formatting for the dashboard charts. Kept out of Charts.tsx
 * so that file exports only components.
 *
 * Shared with the Banter dashboard, minus its month helpers: nothing here is
 * billed monthly, so every series on this page is on a day or a week axis.
 */

export type Point = {
  /** X-axis label, shown in the tooltip and (sparsely) on the axis. */
  label: string
  value: number
  /** Optional extra line in the tooltip, e.g. "12 of 104 completed". */
  note?: string
}

export function fmt(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—'
  return n.toLocaleString('en-US', { maximumFractionDigits: digits })
}

/**
 * Rounds an axis maximum up to a value a human would have chosen.
 *
 * Small counts snap to an even integer so the midpoint gridline lands on a
 * whole number — otherwise a max of 1 labels its three ticks "0, 1, 1".
 */
export function niceCeil(n: number): number {
  if (n <= 0) return 1
  if (n <= 10) return n <= 2 ? Math.ceil(n) : Math.ceil(n / 2) * 2
  const mag = 10 ** Math.floor(Math.log10(n))
  for (const step of [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]) {
    if (n <= step * mag) return step * mag
  }
  return 10 * mag
}

/**
 * Pads a sparse `{day, …}` series out to one entry per calendar day.
 *
 * Every daily view in the analytics schema emits rows only for days that had
 * activity. Plotted raw, a fortnight of silence renders as a single confident
 * line segment between two busy days — which is the opposite of what happened.
 */
export function fillDays<T extends { day: string }>(
  rows: T[],
  days: number,
  pick: (row: T | undefined, day: string) => Point,
): Point[] {
  const byDay = new Map(rows.map(r => [r.day, r]))
  const out: Point[] = []
  const today = new Date()
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i))
    const key = d.toISOString().slice(0, 10)
    out.push(pick(byDay.get(key), key))
  }
  return out
}

/**
 * Which x positions get a label: roughly one per 90px, plus the final point
 * unless it would land on top of the previous one.
 */
export function labelIndices(count: number, plotWidth: number): number[] {
  const every = Math.max(1, Math.ceil(count / Math.max(2, Math.floor(plotWidth / 90))))
  const out: number[] = []
  for (let i = 0; i < count; i += every) out.push(i)
  const last = count - 1
  if (last > 0 && last - out[out.length - 1] >= every * 0.6) out.push(last)
  return out
}

/** Axis ticks print exactly, so a midpoint of 7.5 never renders as "8". */
export function tickLabel(value: number, decimals: number): string {
  return fmt(value, Number.isInteger(value) ? 0 : Math.max(decimals, 1))
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "2026-08-09", 89 → "2026-05-12".
 *
 * Shifts a date the payload already carries rather than reading the clock, so
 * a window derived from it is both pure and anchored to the same day the
 * numbers were counted on.
 */
export function daysBefore(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString().slice(0, 10)
}

/** Whole days from `a` to `b`, both ends counted: "2026-08-01","2026-08-03" → 3. */
export function daysInclusive(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000) + 1
}

/** "2026-08-02" → "Aug 2". */
export function shortDay(iso: string): string {
  const [, m, d] = iso.split('-')
  return `${MONTHS[Number(m) - 1]} ${Number(d)}`
}
